import { type PayloadAction } from '@reduxjs/toolkit';
import {
  type SetDiagramConfigurationProps,
  type SetDiagramFilterProps,
  type SetDiagramRegisterAggregationProps,
  type SetRegisterVisualisationTypeProps,
} from '@common/store/actions/types';
import {
  type GraphsPayloadAction,
  type GraphsStoreState,
} from '@common/store/reducers/graphs/types';
import { type InstallationId } from '@common/types/brandedTypes';
import { configurationForDataPoint } from '@common/types/models/SelectedDataPoints';

export const toggleStatisticsComparisonReducer = (
  compositeState: GraphsStoreState,
  action: PayloadAction<InstallationId>,
) => {
  const installationId = action.payload;
  const state = compositeState.statistics;

  const newState = !state.graphConfiguration[installationId]?.withComparison;
  state.graphConfiguration[installationId] = {
    ...(state.graphConfiguration[installationId] ?? {}),
    withComparison: newState,
  };
};

export const setVisualisationTypeReducer = (
  compositeState: GraphsStoreState,
  action: PayloadAction<SetRegisterVisualisationTypeProps>,
) => {
  const {
    installationId,
    registerIndex,
    valueBitMask,
    type,
  } = action.payload;
  const state = compositeState.diagram;

  Object.values(state.selectedRegisterDataSeriesGraphs[installationId])
    .forEach((graph) =>
      graph.registerDataSeriesArray
        .filter((item) =>
          item.registerIndex === registerIndex
          && (valueBitMask === undefined || item.valueBitMask === valueBitMask)
        )
        .forEach((item) => item.visualisationType = type)
    );
};

export const setDiagramConfigurationReducer = (
  compositeState: GraphsStoreState,
  action: PayloadAction<SetDiagramConfigurationProps>,
) => {
  const {
    installationId,
    range,
    resolution,
  } = action.payload;
  const state = compositeState.diagram;

  state.graphConfiguration[installationId] = {
    range,
    resolution,
  };

  state.selectedRegisterDataSeriesGraphs[installationId]?.forEach((graph) =>
    graph.registerDataSeriesArray = graph.registerDataSeriesArray.map((registerDataSeries) => ({
      ...registerDataSeries,
      ...configurationForDataPoint(state.graphConfiguration[installationId]),
    }))
  );
};

export const setDiagramAggregationReducer = (
  compositeState: GraphsStoreState,
  action: PayloadAction<SetDiagramRegisterAggregationProps>,
) => {
  const {
    installationId,
    registerIndex,
    desiredAggregation,
  } = action.payload;
  const state = compositeState.diagram;

  state.selectedRegisterDataSeriesGraphs[installationId].forEach((graph) =>
    graph.registerDataSeriesArray
      .filter((registerDataSeries) => registerDataSeries.registerIndex === registerIndex)
      .forEach((registerDataSeries) => registerDataSeries.desiredAggregation = desiredAggregation)
  );
};

export const setDiagramFilterReducer = (
  compositeState: GraphsStoreState,
  action: GraphsPayloadAction<SetDiagramFilterProps>,
) => {
  const { targetTab, data: { installationId, contextType, filter } } = action.payload;
  const state = compositeState[targetTab];

  if (!state.diagramFilter[installationId]) {
    state.diagramFilter[installationId] = {
      dataPoints: 'all',
      parameters: 'all',
    };
  }
  state.diagramFilter[installationId][contextType] = filter;
};
